import { seedWorkspace } from '../data/seedWorkspace';
import type { MenuCategory, MenuItem, RestaurantWorkspace } from '../types/workspace';
import { normalizeRestaurantSettings } from './restaurantBranding';
import { slugify } from './slugify';

const createId = (prefix: string) =>
  `${prefix}-${typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}`}`;

export const sortCategories = (categories: MenuCategory[]) =>
  [...categories].sort((left, right) => left.sortOrder - right.sortOrder);

export const sortItems = (items: MenuItem[]) =>
  [...items].sort((left, right) => left.sortOrder - right.sortOrder);

export const reorderList = <T extends { sortOrder: number }>(items: T[], fromIndex: number, toIndex: number) => {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems.map((item, index) => ({
    ...item,
    sortOrder: index,
  }));
};

export const createEmptyCategory = (): MenuCategory => ({
  id: createId('category'),
  title: 'New Category',
  slug: `new-category-${Math.floor(Math.random() * 1000)}`,
  shortDescription: '',
  coverImage: '',
  sortOrder: 0,
  isVisible: true,
});

export const createEmptyItem = (categoryId: string): MenuItem => ({
  id: createId('item'),
  categoryId,
  name: 'New Item',
  slug: `new-item-${Math.floor(Math.random() * 1000)}`,
  price: 0,
  description: '',
  imageUrl: '',
  imageAlt: '',
  sortOrder: 0,
  isAvailable: true,
  isVisible: true,
});

export const syncCategorySlug = (category: MenuCategory): MenuCategory => ({
  ...category,
  slug: slugify(category.slug || category.title) || slugify(category.title),
});

export const syncItemSlug = (item: MenuItem): MenuItem => ({
  ...item,
  slug: slugify(item.slug || item.name) || slugify(item.name),
});

const BREAKFAST_IMAGES = [
  '/images/breakfast-1.jpg',
  '/images/breakfast-2.jpg',
  '/images/breakfast-3.jpg',
  '/images/breakfast-4.jpg',
  '/images/breakfast-5.jpg',
  '/images/breakfast-6.jpg',
];

const SALAD_IMAGES = [
  '/images/salad-1.jpg',
  '/images/salad-2.jpg',
  '/images/salad-3.jpg',
  '/images/salad-4.jpg',
  '/images/salad-5.jpg',
];

const BURGER_IMAGES = [
  '/images/burger-1.jpg',
  '/images/burger-2.jpg',
  '/images/burger-3.jpg',
  '/images/burger-4.jpg',
  '/images/burger-5.jpg',
];

const SANDWICH_IMAGES = [
  '/images/sandwich-1.jpg',
  '/images/sandwich-2.jpg',
  '/images/sandwich-3.jpg',
  '/images/sandwich-4.jpg',
];

const PIZZA_IMAGES = [
  '/images/pizza-1.jpg',
  '/images/pizza-2.jpg',
  '/images/pizza-3.jpg',
  '/images/pizza-4.jpg',
  '/images/pizza-5.jpg',
];

const PASTA_IMAGES = [
  '/images/pasta-1.jpg',
  '/images/pasta-2.jpg',
  '/images/pasta-3.jpg',
  '/images/pasta-4.jpg',
];

const DRINK_IMAGES = [
  '/images/drinks-1.jpg',
  '/images/drinks-2.jpg',
  '/images/drinks-3.jpg',
  '/images/drinks-4.jpg',
  '/images/drinks-5.jpg',
  '/images/drinks-6.jpg',
  '/images/drinks-7.jpg',
  '/images/drinks-8.jpg',
];

const hashString = (value: string) =>
  [...value].reduce((total, character) => total + character.charCodeAt(0), 0);

const pickDeterministic = (options: string[], seed: string) =>
  options[hashString(seed) % options.length];

export const getSuggestedMenuItemImage = (
  item: Pick<MenuItem, 'name' | 'categoryId'>,
  category?: Pick<MenuCategory, 'title' | 'coverImage'>,
) => {
  const normalizedName = item.name.trim().toLowerCase();
  const normalizedCategoryId = item.categoryId.trim().toLowerCase();
  const seed = `${normalizedCategoryId}:${normalizedName}`;

  if (
    normalizedCategoryId === 'breakfast' ||
    /(omelet|egg|foul|oats|pancake|toast|ferfer|fett?ira|chechebsa|melewa|scramble)/.test(normalizedName)
  ) {
    return pickDeterministic(BREAKFAST_IMAGES, seed);
  }

  if (/(burger)/.test(normalizedName)) {
    return pickDeterministic(BURGER_IMAGES, seed);
  }

  if (/(sandwich)/.test(normalizedName)) {
    return pickDeterministic(SANDWICH_IMAGES, seed);
  }

  if (
    normalizedCategoryId === 'salad-vegetable' ||
    /(salad|vegetable)/.test(normalizedName)
  ) {
    return pickDeterministic(SALAD_IMAGES, seed);
  }

  if (normalizedCategoryId === 'pizza' || /pizza/.test(normalizedName)) {
    return pickDeterministic(PIZZA_IMAGES, seed);
  }

  if (
    normalizedCategoryId === 'spaghetti-rice-penne' ||
    normalizedCategoryId === 'soup' ||
    /(spaghetti|rice|penne|carbonara|soup)/.test(normalizedName)
  ) {
    return pickDeterministic(PASTA_IMAGES, seed);
  }

  if (
    normalizedCategoryId === 'juice' ||
    normalizedCategoryId === 'hot-drinks' ||
    normalizedCategoryId === 'soft-drinks' ||
    /(juice|tea|coffee|macchiato|cappuccino|milk|water|cola|pepsi|soft drink|lemon)/.test(normalizedName)
  ) {
    return pickDeterministic(DRINK_IMAGES, seed);
  }

  if (
    normalizedCategoryId === 'arabian-foods' ||
    normalizedCategoryId === 'fish' ||
    normalizedCategoryId === 'stick' ||
    normalizedCategoryId === 'beef' ||
    normalizedCategoryId === 'extra'
  ) {
    return category?.coverImage?.trim() || '/arab-ali/arabian-foods.jpeg';
  }

  return category?.coverImage?.trim() || seedWorkspace.settings.coverImage;
};

/** Ensures newer fields exist when loading older JSON from the API. */
export const normalizeWorkspacePayload = (workspace: RestaurantWorkspace): RestaurantWorkspace => {
  const categoriesById = new Map(workspace.categories.map((category) => [category.id, category]));

  return {
    ...workspace,
    settings: {
      ...normalizeRestaurantSettings({
        ...seedWorkspace.settings,
        ...workspace.settings,
      }),
      homepageIntro:
        typeof workspace.settings.homepageIntro === 'string'
          ? workspace.settings.homepageIntro
          : seedWorkspace.settings.homepageIntro,
    },
    mediaAssets: workspace.mediaAssets ?? [],
    items: workspace.items.map((item) => {
      const category = categoriesById.get(item.categoryId);
      const imageUrl = item.imageUrl?.trim() || getSuggestedMenuItemImage(item, category);

      return {
        ...item,
        imageUrl,
        imageAlt:
          typeof item.imageAlt === 'string' && item.imageAlt.trim()
            ? item.imageAlt
            : `${item.name} photo`,
      };
    }),
  };
};
